import { IGroupItemsOnboardPostBody } from "@/api_framework/api_modals/Inventory";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import Toggle from "@/app/components/ui/Toggles";
import { useGroupItemsActionAPI } from "@/app/hooks/api_hooks/Inventory/GroupItems/useGroupItemsActionAPI";
import { useGroupItemsDetailAPI } from "@/app/hooks/api_hooks/Inventory/GroupItems/useGroupItemsDetailAPI";
import { useGroupItemsOnboardAPI } from "@/app/hooks/api_hooks/Inventory/GroupItems/useGroupItemsOnboardAPI";
import { useGroupItemsUpdateAPI } from "@/app/hooks/api_hooks/Inventory/GroupItems/useGroupItemsUpdateAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { IoIosAddCircle } from "react-icons/io";
import { IoTrashOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
export interface IItemGroupProps {
  parentType: string;
  parentId: string;
  parentItem: string;
  childItems: {
    childType: string;
    childId: string;
    childItem: string;
  }[];
  status: boolean;
}

const GROUP_ITEM_TYPES = [
  { id: "PRODUCT", value: "PRODUCT" },
  { id: "SERVICE", value: "SERVICE" },
  { id: "BOM", value: "BOM" },
];

const ItemGroupSetupOnboard = () => {
  const params = useParams();
  const typeItems = useSelectMenuReducer(GROUP_ITEM_TYPES, "value", "id");
  const [_isActive, setIsActive] = useState(false);
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { execute: searchInSection } = useGroupItemsActionAPI();
  const { execute: createGroupItem } = useGroupItemsOnboardAPI();
  const { execute: fetchGroupItemsDetail, groupItemDetail } = useGroupItemsDetailAPI();
  const { execute: updateGroupItem } = useGroupItemsUpdateAPI();

  const formHook = useForm<IItemGroupProps>({
    mode: "onChange",
    defaultValues: {
      childItems: [
        {
          childType: "",
          childId: "",
          childItem: null,
        },
      ],
    },
  });

  const { control } = formHook;
  const { fields, append, remove } = useFieldArray({
    name: "childItems",
    control,
  });

  useEffect(() => {
    if (params["item_group_id"]) {
      fetchGroupItemsDetail(params["item_group_id"]);
    }
  }, []);

  const handleSearch = (searchValue) => {
    const parentType = formHook.getValues("parentType");
    return searchInSection(parentType, searchValue);
  };

  const handleSearchChild = (searchValue: string, index: number) => {
    const childType = formHook.getValues(`childItems.${index}.childType`);
    return searchInSection(childType, searchValue);
  };

  const addChildItem = () => {
    append({
      childType: "",
      childId: "",
      childItem: "",
    });
  };

  const deleteChildItem = (index: number) => {
    remove(index);
  };

  const handelBackStepper = () => {
    navigate(getRouteKey("ITEM_GROUP_SETUP", "url"));
  };

  useEffect(() => {
    if (Object.keys(groupItemDetail)?.length > 0) {
      formHook.setValue("parentType", groupItemDetail?.parent_details?.type || "");
      formHook.setValue("parentId", groupItemDetail?.parent_details?.selected_item_id || "");
      formHook.setValue("parentItem", groupItemDetail?.parent_details?.selected_item_name || "");
      formHook.setValue("status", groupItemDetail?.status === "active");
      const childLineItems = groupItemDetail?.child_details?.map((childItem) => ({
        childType: childItem?.type || "",
        childId: childItem?.selected_item_id || "",
        childItem: childItem?.selected_item_name || "",
      }));
      formHook.setValue("childItems", childLineItems || []);
    }
  }, [groupItemDetail]);

  const onSubmit = (data: IItemGroupProps) => {
    if (data) {
      const constructedBody: IGroupItemsOnboardPostBody = {
        parent_details: {
          type: data?.parentType,
          selected_item_id: data?.parentId,
        },
        child_details:
          data?.childItems.map((childItem) => ({
            type: childItem?.childType,
            selected_item_id: childItem?.childId,
          })) || [],
        status: data?.status ? "active" : "inactive",
      };
      if (params["item_group_id"]) {
        updateGroupItem(constructedBody, params["item_group_id"]).then(() => {
          navigate(`/app/inventory/item-group-setup`);
        });
      } else {
        createGroupItem(constructedBody).then(({ status }) => {
          if (status) {
            navigate(`/app/inventory/item-group-setup`);
          }
        });
      }
    }
  };

  return (
    <div className="relative mt-6 px-4 sm:px-6">
      <div className="border-gray-200 pb-5 border-b">
        <h3 className="font-semibold text-base text-gray-900 leading-6">Item Group Setup</h3>
        <p className="mt-2 max-w-4xl text-gray-500 text-sm">Add item group for the inventories</p>
      </div>
      <form className="mt-4 sm:mt-8 bg-white py-4 rounded-md w-full" onSubmit={formHook.handleSubmit(onSubmit, (err) => console.log(err))}>
        <p className="text-md font-semibold">Parent Details</p>
        <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
          <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
            <span className="font-normal text-black text-xs">Type</span>
            <span className="font-normal text-rose-600 text-xs">*</span>
          </div>
          <div className="w-full sm:w-[300px]">
            <SearchableSelectMenu
              errorMessages={[{ message: "", type: "required" }]}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("parentType", item.title);
                  formHook.setValue("parentItem", "");
                }
              }}
              fieldError={formHook.formState.errors.parentType}
              register={formHook.register("parentType", { required: true })}
              selectItems={typeItems}
              placeholder="Select Type"
              showTooltips={true}
              showTypedErrors={true}
              showDropdownIcon={true}
              defaultSelected={typeItems?.filter((oc) => oc.title === formHook.watch("parentType"))[0]}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
          <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
            <span className="font-normal text-black text-xs">Select Item</span>
            <span className="font-normal text-rose-600 text-xs">*</span>
          </div>
          <div className="w-full sm:w-[300px]">
            <SearchableSelectMenu
              errorMessages={[
                {
                  message: "Select Item Is Required",
                  type: "required",
                },
              ]}
              placeholder="Search Item"
              fieldError={formHook?.formState?.errors?.parentItem}
              register={formHook.register("parentItem", {
                required: "Please select an item",
                minLength: { value: 2, message: "At least 2 characters are required" },
              })}
              onSelectItem={(item) => {
                formHook.setValue("parentItem", item?.title ?? "");
                formHook.setValue("parentId", item?.id ?? "");
                formHook.trigger("parentItem");
              }}
              withAPISearch={{
                apiCallback: handleSearch,
                reducerIdKey: "id",
                reducerTitleKey: "name",
              }}
              defaultSelectedItemForAPISearch={{
                title: formHook.watch("parentItem"),
                id: formHook.watch("parentId"),
              }}
              showDropdownIcon
              showTypedErrors={true}
            />
          </div>
        </div>
        <hr className="mt-10" />
        <p className="text-md font-semibold mt-5">Child Products/Services</p>
        {fields?.map((item, index) => (
          <div key={item.id} className="relative mt-5 flex flex-col gap-4 w-full border border-gray-200 rounded-md p-4 sm:p-5">
            {index !== 0 && (
              <div className="absolute top-2 right-2 cursor-pointer" onClick={() => deleteChildItem(index)}>
                <IoTrashOutline color="red" size={20} />
              </div>
            )}
            <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline">
              <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                <span className="font-normal text-black text-xs">Type</span>
                <span className="font-normal text-rose-600 text-xs">*</span>
              </div>
              <div className="w-full sm:w-[300px]">
                <SearchableSelectMenu
                  errorMessages={[{ message: "", type: "required" }]}
                  onSelectItem={(item) => {
                    if (item) {
                      formHook.setValue(`childItems.${index}.childType`, item.title);
                      formHook.setValue(`childItems.${index}.childItem`, "");
                    }
                  }}
                  fieldError={formHook?.formState?.errors?.childItems?.[index]?.childType}
                  register={formHook.register(`childItems.${index}.childType`, { required: true })}
                  selectItems={typeItems}
                  placeholder="Select Type"
                  showTooltips={true}
                  showTypedErrors={true}
                  showDropdownIcon={true}
                  defaultSelected={
                    typeItems?.filter((oc) => oc.title === formHook.watch(`childItems.${index}.childType`))[0]
                  }
                  listBoxClassName="w-full"
                  className="text-gray-800"
                  containerClassName="w-full"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
              <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
                <span className="font-normal text-black text-xs">Select Item</span>
                <span className="font-normal text-rose-600 text-xs">*</span>
              </div>
              <div className="w-full sm:w-[300px]">
                <SearchableSelectMenu
                  errorMessages={[
                    {
                      type: "required",
                      message: "Select Item Is Required",
                    },
                  ]}
                  placeholder="Search Item"
                  fieldError={formHook?.formState?.errors?.childItems?.[index]?.childItem}
                  register={formHook.register(`childItems.${index}.childItem`, {
                    required: true,
                    min: 2,
                    minLength: 2,
                  })}
                  onSelectItem={(item) => {
                    formHook.setValue(`childItems.${index}.childItem`, item.title);
                    formHook.setValue(`childItems.${index}.childId`, item.id);
                    formHook.clearErrors([`childItems.${index}.childId`, `childItems.${index}.childItem`]);
                  }}
                  withAPISearch={{
                    apiCallback: (searchValue: string) => {
                      return handleSearchChild(searchValue, index);
                    },
                    reducerIdKey: "id",
                    reducerTitleKey: "name",
                  }}
                  defaultSelectedItemForAPISearch={{
                    title: formHook.watch(`childItems.${index}.childItem`),
                    id: formHook.watch(`childItems.${index}.childId`),
                  }}
                  showDropdownIcon
                  showTypedErrors={true}
                />
              </div>
            </div>
          </div>
        ))}
        <div className="flex gap-3 mt-3 items-center cursor-pointer" onClick={addChildItem}>
          <IoIosAddCircle color="rgba(0, 104, 255, 1)" size={22} />
          <p className="text-[#0267fe] text-sm font-medium">Add Another Child</p>
        </div>
        <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-center mt-5">
          <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
            <span className="font-normal text-black text-xs">Status</span>
          </div>
          <Toggle
            checked={formHook.watch("status")}
            onToggle={(state: boolean) => {
              setIsActive(state);
              formHook.setValue("status", state);
            }}
          />
        </div>
        <div className="bottom-0 right-0 sticky flex flex-row justify-end items-center gap-3 sm:gap-x-4 bg-white mt-8 px-2 py-3 border-t-2 w-full">
          <button type="button" className={SECONDARY_BUTTON} onClick={() => handelBackStepper()}>
            Cancel
          </button>
          <button type="submit" className={PRIMARY_BUTTON}>
            Save & Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default ItemGroupSetupOnboard;
